import { useEffect, useId, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { GlobovmsLogo } from "../components/brand/GlobovmsLogo";
import { Icon } from "../components/icons";
import { useSession } from "../context/Session";

const inviteChecks = new Map();
const inputClass = "h-11 w-full rounded-lg border border-slate-200 bg-slate-50 pl-10 text-sm text-brand-ink transition duration-200 focus:border-brand-blue focus:bg-white";

function verifyInvite(tokenHash, type) {
  const key = `${type}:${tokenHash}`;
  if (!inviteChecks.has(key)) {
    inviteChecks.set(key, supabase.auth.verifyOtp({ token_hash: tokenHash, type }));
  }
  return inviteChecks.get(key);
}

function TextField({ id, label, icon, action, children }) {
  return (
    <div>
      <div className="mb-1.5 flex items-baseline justify-between gap-3">
        <label htmlFor={id} className="text-sm font-semibold text-slate-700">{label}</label>
        {action}
      </div>
      <div className="relative">
        <span className="pointer-events-none absolute inset-y-0 left-0 flex w-10 items-center justify-center text-slate-400">
          <Icon name={icon} className="h-[18px] w-[18px]" />
        </span>
        {children}
      </div>
    </div>
  );
}

function AuthFrame({ children }) {
  return (
    <div className="flex min-h-screen bg-[#f4f7fb]">
      <aside className="relative hidden w-[46%] max-w-xl flex-col justify-between overflow-hidden bg-[#0B1C33] px-12 py-10 text-white lg:flex">
        <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-brand-blue/20" />
        <div className="pointer-events-none absolute -bottom-20 -left-10 h-64 w-64 rounded-full bg-brand-orange/15" />
        <div className="relative"><GlobovmsLogo light /></div>
        <div className="relative">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-brand-blue">Organization</p>
          <h2 className="mt-3 max-w-sm text-4xl font-extrabold leading-tight tracking-tight">Visitor operations, in one place.</h2>
          <p className="mt-4 max-w-sm text-sm leading-6 text-slate-300">Invite visitors, approve arrivals, and see who is on site.</p>
          <ul className="mt-8 space-y-3 text-sm text-slate-200">
            <li className="flex items-center gap-3"><span className="h-1.5 w-1.5 rounded-full bg-brand-orange" />Visitors, hosts, and approvals</li>
            <li className="flex items-center gap-3"><span className="h-1.5 w-1.5 rounded-full bg-brand-blue" />Sites and kiosk status</li>
            <li className="flex items-center gap-3"><span className="h-1.5 w-1.5 rounded-full bg-white" />Reports and audit history</li>
          </ul>
        </div>
        <p className="relative text-xs text-slate-400">Globotech Visitor Management</p>
      </aside>
      <main className="flex flex-1 items-center justify-center px-4 py-10">{children}</main>
    </div>
  );
}

export default function Login() {
  const { configured } = useSession();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("signin");
  const emailId = useId();
  const passwordId = useId();
  const reset = mode === "reset";

  useEffect(() => {
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    if (hash.get("error_code") === "otp_expired") {
      setError("This invitation link is no longer valid. Send a new invite, then open the newest email.");
    }
  }, []);

  function switchMode() {
    setMode(reset ? "signin" : "reset");
    setShowPassword(false);
    setError("");
    setNotice("");
  }

  async function onSubmit(event) {
    event.preventDefault();
    setBusy(true);
    setError("");
    setNotice("");
    if (reset) {
      const redirectTo = `${window.location.origin}/login`;
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
      setBusy(false);
      if (resetError) setError(resetError.message);
      else setNotice("If the account exists, a password reset email is on its way.");
      return;
    }
    const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
    setBusy(false);
    if (signInError) setError(signInError.message);
  }

  return (
    <AuthFrame>
      <form className="w-full max-w-md overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" onSubmit={onSubmit}>
        <div className="h-1 bg-brand-orange" />
        <div className="p-8">
          <div className="mb-6 lg:hidden"><GlobovmsLogo /></div>
          <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-sky-50 text-brand-blue">
            <Icon name={reset ? "email" : "lock"} className="h-5 w-5" />
          </div>
          <h1 className="mt-4 text-2xl font-extrabold tracking-tight text-brand-ink">{reset ? "Reset password" : "Sign in"}</h1>
          <p className="mt-1.5 text-sm leading-6 text-slate-500">
            {reset ? "We will email a reset link if this address belongs to an organization account." : "Sign in with the account your organization invited."}
          </p>
          {!configured ? <p className="mt-5 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm text-rose-800" role="alert">Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY before signing in.</p> : null}
          {error ? <p className="mt-5 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm text-rose-800" role="alert">{error}</p> : null}
          {notice ? <p className="mt-5 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2.5 text-sm text-sky-900" role="status">{notice}</p> : null}
          <div className="mt-6 space-y-5">
            <TextField id={emailId} label="Email" icon="email">
              <input id={emailId} className={inputClass} type="email" value={email} onChange={(event) => setEmail(event.target.value)} required autoComplete="username" />
            </TextField>
            {reset ? null : (
              <TextField
                id={passwordId}
                label="Password"
                icon="lock"
                action={<button type="button" className="text-xs font-semibold text-brand-blue hover:underline" onClick={switchMode}>Forgot password</button>}
              >
                <input id={passwordId} className={`${inputClass} pr-11`} type={showPassword ? "text" : "password"} value={password} onChange={(event) => setPassword(event.target.value)} required autoComplete="current-password" />
                <button type="button" className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-r-lg text-slate-400 hover:text-slate-700" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? "Hide password" : "Show password"} aria-pressed={showPassword}>
                  <Icon name={showPassword ? "eyeOff" : "eye"} className="h-[18px] w-[18px]" />
                </button>
              </TextField>
            )}
            <button className="flex h-11 w-full items-center justify-center rounded-lg bg-brand-orange text-sm font-semibold text-white transition duration-200 hover:bg-[#c4631d] disabled:cursor-not-allowed disabled:opacity-50" disabled={busy || !configured}>{busy ? "Please wait" : reset ? "Send reset link" : "Sign in"}</button>
            {reset ? <button type="button" className="w-full text-sm font-semibold text-brand-blue hover:underline" onClick={switchMode}>Back to sign in</button> : null}
          </div>
        </div>
      </form>
    </AuthFrame>
  );
}

export function AcceptInvite({ tokenHash, type, onDone }) {
  const navigate = useNavigate();
  const [phase, setPhase] = useState("checking");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const passwordId = useId();

  useEffect(() => {
    let cancelled = false;
    const allowed = ["invite", "signup", "magiclink", "recovery"];
    if (!allowed.includes(type)) {
      setPhase("error");
      setError("This invitation link is not valid.");
      return undefined;
    }
    verifyInvite(tokenHash, type).then(({ error: verifyError }) => {
      if (cancelled) return;
      if (verifyError) {
        setPhase("error");
        setError("This invitation link is no longer valid. Send a new invite, then open the newest email.");
        return;
      }
      setPhase("password");
    });
    return () => { cancelled = true; };
  }, [tokenHash, type]);

  async function savePassword(event) {
    event.preventDefault();
    setBusy(true);
    setError("");
    const { error: updateError } = await supabase.auth.updateUser({ password });
    setBusy(false);
    if (updateError) {
      setError(updateError.message);
      return;
    }
    onDone();
    navigate("/", { replace: true });
  }

  return (
    <AuthFrame>
      <form onSubmit={savePassword} className="w-full max-w-md overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="h-1 bg-brand-orange" />
        <div className="p-8">
          <div className="mb-6 lg:hidden"><GlobovmsLogo /></div>
          <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-sky-50 text-brand-blue">
            <Icon name="lock" className="h-5 w-5" />
          </div>
          <h1 className="mt-4 text-2xl font-extrabold tracking-tight text-brand-ink">{phase === "password" ? "Choose a password" : phase === "error" ? "Invitation link" : "Accepting invitation"}</h1>
          <p className="mt-1.5 text-sm leading-6 text-slate-500">
            {phase === "password" ? "This password is how you will sign in to the portal next time." : phase === "error" ? "Use the newest invitation email." : "Checking the invitation link."}
          </p>
          {error ? <p className="mt-5 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 text-sm text-rose-800" role="alert">{error}</p> : null}
          {phase === "password" ? (
            <div className="mt-6 space-y-5">
              <TextField id={passwordId} label="Password" icon="lock">
                <input id={passwordId} className={`${inputClass} pr-11`} type={showPassword ? "text" : "password"} value={password} onChange={(event) => setPassword(event.target.value)} required minLength={8} autoComplete="new-password" />
                <button type="button" className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-r-lg text-slate-400 hover:text-slate-700" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? "Hide password" : "Show password"} aria-pressed={showPassword}>
                  <Icon name={showPassword ? "eyeOff" : "eye"} className="h-[18px] w-[18px]" />
                </button>
              </TextField>
              <button className="flex h-11 w-full items-center justify-center rounded-lg bg-brand-orange text-sm font-semibold text-white transition duration-200 hover:bg-[#c4631d] disabled:opacity-50" disabled={busy}>{busy ? "Saving" : "Save password"}</button>
            </div>
          ) : null}
        </div>
      </form>
    </AuthFrame>
  );
}
